import { isAnalyticsHost } from "@/lib/analytics/analyticsHost";
import { AnalyticsEvent } from "@/lib/analytics/events";

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

/**
 * EEA, UK and Swiss visitors are measured without cookies (Consent Mode v2
 * "denied"): GA gets cookieless pings and models the rest. There is no
 * consent banner, so storage stays denied there. Everywhere else analytics
 * and ad storage are granted, so Google Ads can attribute conversions.
 */
const CONSENT_REQUIRED_REGIONS = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IS",
  "IE",
  "IT",
  "LV",
  "LI",
  "LT",
  "LU",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "GB",
  "CH",
];

/** GA4 recommended event names, so Google Ads can import them as conversions. */
const GA_EVENT_NAMES: Partial<Record<AnalyticsEvent, string>> = {
  [AnalyticsEvent.CheckoutStarted]: "begin_checkout",
  [AnalyticsEvent.SummaryShared]: "share",
};

let configured = false;
let scriptLoaded = false;

function gtag(..._args: unknown[]): void {
  window.dataLayer ??= [];
  // gtag.js reads the arguments object itself, not an array copy.
  // biome-ignore lint/complexity/noArguments: required by gtag.js
  window.dataLayer.push(arguments);
}

function measurementId(): string | null {
  const id = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  return id && isAnalyticsHost() ? id : null;
}

/** Sets up the gtag queue and config, so events before the script are kept. */
function ensureConfigured(): boolean {
  const id = measurementId();
  if (!id) return false;
  if (configured) return true;
  configured = true;

  window.gtag = gtag;
  gtag("consent", "default", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    region: CONSENT_REQUIRED_REGIONS,
  });
  gtag("js", new Date());
  gtag("config", id);
  return true;
}

/**
 * Downloads gtag.js once. Call it after the page has loaded so it never
 * competes with first paint.
 */
export function loadGoogleAnalytics(): void {
  if (scriptLoaded || !ensureConfigured()) return;
  scriptLoaded = true;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId()}`;
  document.head.appendChild(script);
}

/** Mirrors a product analytics event to GA4. */
export function captureGoogleAnalyticsEvent(
  event: AnalyticsEvent,
  properties?: Record<string, string | number | boolean>,
): void {
  if (!ensureConfigured()) return;
  gtag("event", GA_EVENT_NAMES[event] ?? event, properties);
}

/** Records a real sign-in as GA4 `login`; the server sends `sign_up`. */
export function captureGoogleAnalyticsLogin(): void {
  if (!ensureConfigured()) return;
  gtag("event", "login");
}

/** Ties GA4 sessions to the Supabase user id, the id server events use. */
export function setGoogleAnalyticsUser(userId: string): void {
  const id = measurementId();
  if (!id || !ensureConfigured()) return;
  gtag("config", id, { user_id: userId });
}
